/**
 * Settings > General > Firm details: the firm's name, office address, phone
 * and fax. The G-28 and preparer sections of USCIS forms take the firm's
 * name and address from here when the attorney's own office address is
 * blank. Saved as one section.
 */
import { Loader2 } from "lucide-react";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { toast } from "sonner";

import { LawFirmsAPI } from "@/api/lawFirms";
import type { LawFirm, UnitType } from "@/api/types/lawFirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

type Props = {
  lawFirm: LawFirm | null;
  onSaved: (firm: LawFirm) => void;
};

const NO_UNIT = "none";

const TEXT_FIELDS = [
  "name",
  "address",
  "unit_number",
  "city",
  "state",
  "zipcode",
  "province",
  "postal_code",
  "country",
  "phone",
  "fax"
] as const;

type TextKey = (typeof TEXT_FIELDS)[number];

type Draft = Record<TextKey, string> & { unit_type: UnitType | null };

export const firmDraftOf = (firm: LawFirm | null): Draft => ({
  ...(Object.fromEntries(
    TEXT_FIELDS.map((key) => [key, firm?.[key] ?? ""])
  ) as Record<TextKey, string>),
  unit_type: firm?.unit_type ?? null
});

const Field: FC<{ id: string; label: string; children: ReactNode }> = ({
  id,
  label,
  children
}) => (
  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
    <Label htmlFor={id}>{label}</Label>
    {children}
  </div>
);

export const FirmContactSection: FC<Props> = ({ lawFirm, onSaved }) => {
  const [draft, setDraft] = useState<Draft>(firmDraftOf(lawFirm));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(firmDraftOf(lawFirm));
  }, [lawFirm]);

  const text = (key: TextKey, label: string) => (
    <Field id={`firm-${key}`} label={label}>
      <Input
        id={`firm-${key}`}
        value={draft[key]}
        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
      />
    </Field>
  );

  const save = async () => {
    if (!lawFirm) return;
    if (!draft.name.trim()) {
      toast.error("The firm needs a name.");
      return;
    }
    setSaving(true);
    try {
      const updated = await LawFirmsAPI.update(lawFirm.uid, {
        ...Object.fromEntries(
          TEXT_FIELDS.map((key) => [key, draft[key].trim() || null])
        ),
        unit_type: draft.unit_type
      });
      onSaved(updated);
      toast.success("Firm details saved");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not save the firm details."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="flex w-full flex-col gap-3" data-testid="firm-contact">
      <SectionHeader
        title="Firm Details"
        description="Used on the G-28 and other forms when an attorney has no office address of their own."
      />
      <div className="flex flex-col gap-4 rounded-lg bg-elevated p-4 ring-1 ring-line-2">
        {text("name", "Firm Name")}
        {text("address", "Street")}
        <div className="flex gap-3">
          <Field id="firm-unit-type" label="Unit">
            <Select
              value={draft.unit_type ?? NO_UNIT}
              onValueChange={(value) =>
                setDraft((d) => ({
                  ...d,
                  unit_type: value === NO_UNIT ? null : (value as UnitType)
                }))
              }
            >
              <SelectTrigger id="firm-unit-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_UNIT}>None</SelectItem>
                <SelectItem value="apt">Apt.</SelectItem>
                <SelectItem value="ste">Ste.</SelectItem>
                <SelectItem value="flr">Flr.</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {text("unit_number", "Unit Number")}
        </div>
        <div className="flex gap-3">
          {text("city", "City")}
          {text("state", "State")}
          {text("zipcode", "ZIP Code")}
        </div>
        <div className="flex gap-3">
          {text("province", "Province")}
          {text("postal_code", "Postal Code")}
          {text("country", "Country")}
        </div>
        <div className="flex gap-3">
          {text("phone", "Phone")}
          {text("fax", "Fax")}
        </div>
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="primary"
            type="button"
            onClick={() => void save()}
            disabled={saving || !lawFirm}
            data-testid="firm-contact-save"
          >
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            Save Firm Details
          </Button>
        </div>
      </div>
    </section>
  );
};
